'use client'

import { atom, type createStore } from 'jotai'

import { recordStreamPerformanceEvent } from '@/lib/debug/stream-stats'
import { Instrument } from '@/types/generated/common/types'

// Re-export types from types/dashboard for backward compatibility
export type { ChartOption, SelectedInstrument, SelectedOption } from '@/types/dashboard'
export { PositionTableTab } from '@/types/dashboard'

/**
 * Ticker data for watchlist items indexed by the canonical `exchange:symbol` key.
 */
export type WatchListTickerMap = Record<
  string,
  {
    lastPrice?: string
    markPrice?: string
    indexPrice?: string
    prevLastPrice?: string // Previous price for change highlighting
    bestBidPrice?: string
    prevBestBidPrice?: string // Previous bid price for change highlighting
    bestAskPrice?: string
    prevBestAskPrice?: string // Previous ask price highlighting
    volume24h?: string
    priceChange24h?: string
    priceChangePercent24h?: string
    high24h?: string
    low24h?: string
    openInterest?: string
    fundingRate?: string
    /** Unix nanoseconds, as `Ticker.nextFundingTime` — see lib/next-funding. */
    nextFundingTime?: string
    closedPrice?: string
  }
>

export const watchListTickerMapAtom = atom<WatchListTickerMap>({})

/**
 * Per-map `symbol → key` index for findTickerBySymbol. A lookup matches the first key (in
 * insertion order) ending in `:${symbol}`, so every text after each `:` in a key is a
 * candidate symbol — `HYPERLIQUID:xyz:TSLA` answers both `xyz:TSLA` and `TSLA`. Keyed by map
 * identity so each published map is indexed at most once, and flushTickerMapWrites carries
 * the index forward to the next map instead of rebuilding it on every flush.
 */
type TickerSymbolIndex = Map<string, string>
const tickerSymbolIndexes = new WeakMap<WatchListTickerMap, TickerSymbolIndex>()

function indexTickerKey(index: TickerSymbolIndex, key: string) {
  for (let at = key.indexOf(':'); at !== -1; at = key.indexOf(':', at + 1)) {
    const symbol = key.slice(at + 1)
    // First key wins, matching Object.keys(...).find order.
    if (!index.has(symbol)) index.set(symbol, key)
  }
}

function getTickerSymbolIndex(map: WatchListTickerMap): TickerSymbolIndex {
  let index = tickerSymbolIndexes.get(map)
  if (!index) {
    index = new Map()
    for (const key in map) indexTickerKey(index, key)
    tickerSymbolIndexes.set(map, index)
  }
  return index
}

/**
 * Hands `prev`'s index to `next` when `next` only adds keys to `prev` (the flush path's
 * common case). Existing keys keep their insertion order, so adding the new keys' symbols
 * where absent reproduces a fresh build. The shared index may then name a key `prev` lacks;
 * findTickerBySymbol's `key in map` check makes that a miss for `prev`, which is what a scan
 * of `prev` would return, since the symbol had no earlier match there.
 */
function carryTickerSymbolIndex(
  prev: WatchListTickerMap,
  next: WatchListTickerMap,
  addedKeys: Iterable<string>
) {
  const index = tickerSymbolIndexes.get(prev)
  if (!index) return
  for (const key of addedKeys) indexTickerKey(index, key)
  tickerSymbolIndexes.set(next, index)
}

export function findTickerBySymbol(map: WatchListTickerMap, symbol: string) {
  const key = getTickerSymbolIndex(map).get(symbol)
  return key !== undefined && key in map ? map[key] : undefined
}

export const symbolFromTickerKey = (key: string) => key.slice(key.indexOf(':') + 1)

type JotaiStore = ReturnType<typeof createStore>
export type WatchListTicker = WatchListTickerMap[string]
type TickerEntryUpdater = (current: WatchListTicker | undefined) => WatchListTicker | undefined

// The app runs one shared store (lib/configs/store-config), so pending entries
// can be coalesced globally by their canonical exchange:symbol key.
const pendingTickerEntries = new Map<string, WatchListTicker>()
let tickerMapFlushTimer: ReturnType<typeof setTimeout> | null = null
const TICKER_MAP_FLUSH_INTERVAL_MS = 100
export const MAX_PENDING_TICKER_SYMBOLS = 1500

/**
 * Safety ceiling on this map's total entries. Unlike the per-symbol atomFamily caches in
 * market-data-cache.ts (orderbookBidsAtomFamily etc.), this single shared map has no
 * ref-counted eviction — a dozen-plus call sites read it (order-form validation, chart
 * stats row, ticker tape, watchlists, price-title, …) and none of them "owns" a
 * subscription the way an orderbook/recent-trades widget instance does, so there's no
 * single mount/unmount pair to hook a per-key unregister to. Every distinct
 * `${exchange}:${symbol}` any watchlist tab has ever streamed a tick for otherwise stays
 * here for the life of the browser tab.
 *
 * Set well above any realistic count of concurrently-relevant symbols, so normal use
 * (even many open pages/watchlists) never reaches it — this is a backstop against
 * unbounded growth, not a working-set cache. See `capTickerMap` for how eviction picks
 * a safe victim when it does engage.
 */
export const MAX_TICKER_MAP_ENTRIES = 1500
// Last time each key was actually written (not merely present) — see capTickerMap.
const _tickerLastWriteAt = new Map<string, number>()

/**
 * Evicts by last-WRITE time, not last-read or insertion order. A symbol still actively
 * streamed (i.e. still displayed/subscribed anywhere) keeps refreshing its own
 * timestamp on every tick, so eviction can only ever remove entries nothing has updated
 * in a long time — it can't evict something currently on screen out from under it.
 */
export function capTickerMap(
  prev: WatchListTickerMap,
  merged: WatchListTickerMap,
  maxEntries: number = MAX_TICKER_MAP_ENTRIES
): WatchListTickerMap {
  const now = Date.now()
  for (const key in merged) {
    if (merged[key] !== prev[key]) _tickerLastWriteAt.set(key, now)
  }

  const keys = Object.keys(merged)
  const excess = keys.length - maxEntries
  if (excess <= 0) return merged

  const staleFirst = keys.sort(
    (a, b) => (_tickerLastWriteAt.get(a) ?? 0) - (_tickerLastWriteAt.get(b) ?? 0)
  )
  const next = { ...merged }
  for (const key of staleFirst.slice(0, excess)) {
    delete next[key]
    _tickerLastWriteAt.delete(key)
  }
  return next
}

/**
 * Coalesced write path for watchListTickerMapAtom. WS ticker handlers fire as
 * one macrotask per message; writing the atom on every message makes each of
 * its subscribers (watchlist tables, orderbook, ticker tape, order form, …)
 * re-render at the full message rate. When a heavy subscriber is mounted
 * (e.g. the order-form sheet) a single render pass can outlast the gap
 * between messages, so the message queue backs up and rAF/paint starve —
 * widgets look frozen. Queuing updaters here and flushing at most once per
 * TICKER_MAP_FLUSH_INTERVAL_MS puts a hard ceiling on the global re-render
 * rate regardless of message throughput. Updates are applied to each symbol's
 * pending value at enqueue time, preserving sequential semantics (including
 * prev-price highlighting) while retaining only one final entry per symbol.
 */
export function scheduleTickerMapWrite(
  store: JotaiStore,
  key: string,
  updater: TickerEntryUpdater
) {
  const current = pendingTickerEntries.get(key) ?? store.get(watchListTickerMapAtom)[key]
  const next = updater(current)
  if (next === undefined || next === current) return

  pendingTickerEntries.set(key, next)

  // Never drop ticker state: at the hard ceiling, publish the current buffer
  // immediately and begin a fresh window.
  if (pendingTickerEntries.size >= MAX_PENDING_TICKER_SYMBOLS) {
    flushTickerMapWrites(store)
    return
  }
  if (tickerMapFlushTimer !== null) return
  tickerMapFlushTimer = setTimeout(() => flushTickerMapWrites(store), TICKER_MAP_FLUSH_INTERVAL_MS)
}

export function flushTickerMapWrites(store: JotaiStore) {
  if (tickerMapFlushTimer !== null) clearTimeout(tickerMapFlushTimer)
  tickerMapFlushTimer = null
  if (pendingTickerEntries.size === 0) return

  const entries = new Map(pendingTickerEntries)
  pendingTickerEntries.clear()
  const startedAt = performance.now()
  store.set(watchListTickerMapAtom, (prev) => {
    const merged = { ...prev }
    const addedKeys: string[] = []
    entries.forEach((entry, key) => {
      if (!(key in prev)) addedKeys.push(key)
      merged[key] = entry
    })
    const next = capTickerMap(prev, merged)
    // An eviction (next !== merged) removes keys, so the next lookup rebuilds instead.
    if (next === merged) carryTickerSymbolIndex(prev, next, addedKeys)
    return next
  })
  recordStreamPerformanceEvent({
    type: 'ticker-buffer-flush',
    bufferedSymbols: entries.size,
    durationMs: performance.now() - startedAt,
  })
}

// Global layout state (true when any widget is being dragged or resized)
export const isLayoutChangingAtom = atom<boolean>(false)

// Re-export storage atoms for backward compatibility
export {
  DASHBOARD_CONFIG_GLOBAL_SCOPE,
  dashboardConfigAtom,
  dashboardConfigAtomFamily,
  watchListAtom,
  type WatchList,
} from '@/state/storage'
